"use client";

import React, { useEffect } from "react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { X, Mail } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";
const meditationPractices = [{
  id: 1,
  src: "https://huge-sunbird.10web.cloud/wp-content/uploads/2026/03/lake_tile5.webp?v=ku1241iec3b",
  title: "4 Bereiche der Achtsamkeit",
  shortDesc: "Körper, Atem, Gefühle und Geist.",
  fullDesc: "Die Satipatthana sind die vier Felder der Achtsamkeit: Die Beobachtung des Körpers, der Gefühle, des Geistes und der Geistobjekte."
}, {
  id: 2,
  src: "https://huge-sunbird.10web.cloud/wp-content/uploads/2026/03/post_workshop-ee022e3e.webp?v=itxgpqab3l",
  title: "Gehmeditation",
  shortDesc: "Achtsamkeit bei jedem Schritt.",
  fullDesc: "Wir spüren den Kontakt der Füße zum Boden und finden Ruhe in der Bewegung – ganz im Hier und Jetzt."
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587884/img/lake_tile3.png",
  title: "Body Scan",
  shortDesc: "Körperwahrnehmung und Loslassen.",
  fullDesc: "In dieser Körperreise wandern wir mit der Aufmerksamkeit durch den gesamten Organismus, um Spannungen liebevoll loszulassen."
}, {
  id: 4,
  src: "https://huge-sunbird.10web.cloud/wp-content/uploads/2026/03/water_nature_gallery_drops.webp?v=qlh73vcvdok",
  title: "Achtsamkeit im Alltag",
  shortDesc: "Präsenz in jeder Handlung.",
  fullDesc: "Ob beim Essen oder im Wald: Wir schalten den Autopiloten aus. Besonders die Verbindung zur Natur hilft uns dabei."
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587884/img/tai_chi_practice.png",
  title: "Meditation in Bewegung",
  shortDesc: "Tai Chi, Qi Gong und Yoga.",
  fullDesc: "Meditation war schon immer Kern der Kampfkünste. Die Bewegung dient als Brücke, um die Energie fließen zu lassen."
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587884/img/lake_tile2.png",
  title: "Die Brahmaviharas",
  shortDesc: "Güte, Mitgefühl, Mitfreude & Gleichmut.",
  fullDesc: "Diese vier Herzensqualitäten bilden das emotionale Rückgrat deiner Praxis und schützen dich vor dem Lebensdrama."
}, {
  id: 7,
  src: "https://huge-sunbird.10web.cloud/wp-content/uploads/2026/04/IMG_20250808_082131-scaled.jpg?v=25ce2cy231l",
  title: "Ethik & Weisheit",
  shortDesc: "Integrität jenseits der Matte.",
  fullDesc: "Wahre Achtsamkeit zeigt sich in weisen Entscheidungen. Wir lernen, was uns und anderen wirklich guttut."
}];
export default function EnergyFlowYogaPage() {
  // Wir nutzen zwei verschiedene Refs für die Animationen (Hero und Galerie)
  const heroReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const galleryReveal = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    document.title = "Meditation & Achtsamkeit | Claudia Christ";
  }, []);
  return <main className="bg-background font-['Montserrat'] flex flex-col">
      
      {/* --- NEUE HERO SEKTION (ANALOG ZU YOGA) --- */}
      <section className="relative w-full overflow-hidden bg-background text-foreground">
        <div className="absolute inset-0 z-0">
          <div className="h-full w-full bg-[url('https://huge-sunbird.10web.cloud/wp-content/uploads/2026/03/serene_river_flow_stones.webp')] bg-cover bg-center bg-no-repeat" />
        </div>

        {/* Overlay für bessere Lesbarkeit auf Mobilgeräten */}
        <div className="absolute inset-0 z-10 bg-black/30 md:bg-black/20" />

        <div className="relative z-20 mx-auto flex min-h-[600px] max-w-7xl flex-col items-center justify-center px-6 py-[120px] text-center">
          <div ref={heroReveal.ref} className={`flex max-w-4xl flex-col items-center gap-6 transition-all duration-[800ms] ease-out ${heroReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
            <h1 className="font-sans text-[2.8rem] leading-[1.1] tracking-[-0.01em] !text-[hsl(0,0%,100%)] !font-light sm:text-[4.5rem] md:text-[5rem] uppercase">
              Achtsamkeit & Meditation
            </h1>

            <p className="max-w-2xl font-sans text-[1rem] uppercase tracking-[0.2em] !text-[hsl(0,0%,100%)] !font-normal sm:text-[1.25rem]">
              DER GEIST WIE EIN STILLER, KLARER SEE
            </p>
          </div>
        </div>
      </section>

      {/* Intro Sektion */}
      <section className="py-24 md:py-32 text-center bg-white border-b border-[#2C5354]/5">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <h2 className="text-4xl md:text-5xl font-light text-[#2C5354] uppercase mb-12 tracking-tight">Stille und Klarheit</h2>
          <div className="text-lg text-muted-foreground leading-relaxed font-light max-w-3xl mx-auto space-y-6 text-left md:text-center">
            <p>
              Achtsamkeit ist die Fähigkeit, das Bewusstsein auf Vorgänge in der äußeren Welt oder in unserem Inneren zu richten und dabei etwas Neues zu sehen und zu verstehen, das dann hilfreiche Veränderungen in unserem Leben bewirkt. Verwurzelt in der Lehre des historischen Buddha, ist Vipassana-Meditation ein 2600 Jahre lang erprobtes und bewährtes System zur Erforschung, Beruhigung und Klärung des eigenen Geistes.</p>
            <p>
              Wir lernen, einen frischen Blick auf leidbringende Denk- und Verhaltensmuster zu werfen, und einen Lebensstil zu kultivieren, der uns selbst und unserer Mitwelt gut tut. So entwickeln wir die Fähigkeit, dem Leben nicht mit Sorgen, Druck und Drama zu begegnen, sondern mit immer mehr Freundlichkeit, Verantwortung, Respekt, Gelassenheit und Weisheit.</p>
            <p className="pt-4 text-base opacity-80 border-t border-[#2C5354]/10">
              Inspiriert durch Lehrer wie <span className="text-[#2C5354] font-medium">Christopher Titmuss</span> und das moderne, säkulare Stressmanagement nach <span className="text-[#2C5354] font-medium">Jon Kabat-Zinn</span>.
            </p>
          </div>
        </div>
      </section>

      {/* Kachel Galerie */}
      <section className="py-16 md:py-24 bg-[#FAF6F1]/50">
        <div className="container mx-auto px-4">
          <div ref={galleryReveal.ref} className={cn("flex flex-wrap justify-center gap-6 transition-all duration-1000 ease-out", galleryReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
            {meditationPractices.map(item => <Dialog.Root key={item.id}>
                <Dialog.Trigger asChild>
                  <div className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)] max-w-md group relative overflow-hidden rounded-xl bg-white shadow-sm hover:shadow-xl transition-all duration-500 cursor-pointer border border-[#2C5354]/5">
                    <AspectRatio ratio={1}>
                      <img src={item.src} className="object-cover w-full h-full transition-transform duration-1000 group-hover:scale-110" alt="" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
                      <div className="absolute inset-x-0 bottom-0 p-8 text-white text-left">
                        <h3 className="text-xl font-medium uppercase mb-2 tracking-wide leading-tight">{item.title}</h3>
                        <p className="text-sm font-light opacity-90 line-clamp-2 pr-4">{item.shortDesc}</p>
                        <span className="mt-4 inline-block text-[10px] uppercase tracking-[0.2em] border-b border-white/40 pb-1 group-hover:border-white transition-colors">Mehr erfahren</span>
                      </div>
                    </AspectRatio>
                  </div>
                </Dialog.Trigger>
                <Dialog.Portal>
                  <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] animate-in fade-in duration-300" />
                  <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-2xl bg-white rounded-2xl shadow-2xl z-[101] overflow-hidden animate-in zoom-in-95 duration-300">
                    <div className="relative">
                      <img src={item.src} className="w-full h-64 object-cover" alt="" />
                      <Dialog.Close className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full p-2 text-white transition-colors">
                        <X className="size-6" />
                      </Dialog.Close>
                    </div>
                    <div className="p-8 md:p-10">
                      <Dialog.Title className="text-2xl md:text-3xl font-light text-[#2C5354] uppercase tracking-widest mb-6">{item.title}</Dialog.Title>
                      <Dialog.Description className="text-lg text-muted-foreground leading-relaxed font-light">{item.fullDesc}</Dialog.Description>
                      <div className="mt-10 flex justify-end">
                        <Dialog.Close asChild>
                          <button className="px-6 py-2 border border-[#2C5354]/20 rounded-full text-[#2C5354] hover:bg-[#2C5354] hover:text-white transition-all uppercase text-xs tracking-widest font-medium">Schließen</button>
                        </Dialog.Close>
                      </div>
                    </div>
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog.Root>)}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section id="cta-meditation" className="py-24 md:py-32 text-center bg-background border-t border-[#2C5354]/10">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-bold text-[#2C5354] mb-8 uppercase tracking-tight">Zeit für Stille?</h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-light max-w-2xl mx-auto mb-10">
            Möchtest du tiefer in die Welt der Meditation eintauchen oder hast du Fragen zur Praxis? Ich freue mich auf deine Nachricht.
          </p>
          <Button size="lg" asChild className="rounded-full px-10 py-7 text-lg bg-[#2C5354] hover:bg-[#1a3a3b] transition-all shadow-xl">
            <a href="mailto:post@claudiachrist.de?subject=Anfrage Meditation & Achtsamkeit">Schreib mir eine Nachricht <Mail className="ml-3 size-5" /></a>
          </Button>
        </div>
      </section>
    </main>;
}